// src/components/controller/TimerControls.js
import { Button } from 'baseui/button';
import { Input } from 'baseui/input';
import { Slider } from 'baseui/slider';
import {
  TimerTitle,
  TimerValue,
  SliderContainer,
  ControlsContainer,
  StepButtonsContainer,
  StepButtonGroup,
  TimeUnitLabel,
  StepInputOverrides,
} from '../../styles/timerControlsStyles';
import {
  PlayIcon,
  PauseIcon,
  RefreshIcon,
  StepForwardIcon,
  StepBackIcon,
  SkipBackIcon,
  FastForwardIcon,
  RewindIcon,
  ClockPlayIcon,
  TimerOffIcon,
} from '../../styles/iconStyles';
import { SectionContainer, FlexColumnCentered } from '../../styles/controllerPageStyles';
import TooltipWrapper from '../common/TooltipWrapper';
import { formatTimer, parseTime } from '../../utils/viewHelpers';

const TimerControls = ({
  rows,
  rowIndex,
  remainingTime,
  timerState,
  isReadyEditing,
  stepSeconds,
  appConfig,
  t,
  tournamentActions,
  setModals,
  setStepSeconds,
}) => {
  const noRows = rows.length === 0;
  const maxTime = noRows ? 0 : (parseTime(rows[rowIndex]?.[appConfig.requiredTimeKey] || '00:00') || 0);
  

  const playPauseButtonDisabled = noRows || isReadyEditing || timerState === 'stop';
  const forceStopButtonDisabled = (timerState !== 'play' && timerState !== 'pause') || isReadyEditing;
  const resetToRowStartDisabled = noRows || isReadyEditing || remainingTime >= maxTime;
  const prevLevelButtonDisabled = noRows || isReadyEditing || rowIndex === 0;
  const stepBackButtonDisabled = noRows || isReadyEditing || remainingTime >= maxTime;
  const stepForwardButtonDisabled = noRows || isReadyEditing || timerState === 'stop' || (timerState !== 'ready' && remainingTime === 0 && rowIndex >= rows.length - 1);
  const nextLevelButtonDisabled = noRows || isReadyEditing || timerState === 'stop' || (timerState !== 'ready' && rowIndex >= rows.length - 1);
  const inputSecondsDisabled = noRows || isReadyEditing || (stepBackButtonDisabled && stepForwardButtonDisabled);
  const sliderDisabled = timerState === 'stop';
  
  const sliderValue = maxTime > 0 ? Math.max(0, maxTime - remainingTime) : 0;
  

  return (
    <SectionContainer>
      <FlexColumnCentered>
        <TimerTitle>{noRows ? t('display.noStructureLoaded') : (rows[rowIndex]?.type === 'break' ? t('common.break') : `${t('common.level')} ${rows[rowIndex]?.level || ''}`)}</TimerTitle>
        <TimerValue>{formatTimer(remainingTime)}</TimerValue>
      </FlexColumnCentered>
      <SliderContainer>
        {!noRows && (
          <Slider
            value={[sliderValue]}
            min={0}
            max={maxTime > 0 ? maxTime : 1}
            onChange={({ value }) => tournamentActions.adjustTimer(maxTime - value[0] - remainingTime)}
            disabled={sliderDisabled}
            overrides={{
              ThumbValue: { style: { display: 'none' } },
              TickBar: { style: { display: 'none' } },
            }}
          />
        )}
      </SliderContainer>
      <ControlsContainer>
        <TooltipWrapper tooltipContent={t('tournament.resetCurrentLevelTooltip')}><Button onClick={tournamentActions.resetToRowStart} disabled={resetToRowStartDisabled} shape="square"><SkipBackIcon /></Button></TooltipWrapper>
        <TooltipWrapper tooltipContent={timerState === 'play' ? t('common.pause') : t('common.start')}><Button onClick={timerState === 'play' ? tournamentActions.pauseTimer : tournamentActions.startTimer} disabled={playPauseButtonDisabled} shape="square">{timerState === 'play' ? <PauseIcon /> : <PlayIcon />}</Button></TooltipWrapper>
        <TooltipWrapper tooltipContent={t('tournament.scheduleTimerTooltip')}><Button onClick={() => setModals(m => ({ ...m, schedule: true }))} disabled={noRows || timerState !== 'ready' || isReadyEditing} shape="square"><ClockPlayIcon /></Button></TooltipWrapper>
        <TooltipWrapper tooltipContent={t('tournament.backToStartTooltip')}><Button onClick={tournamentActions.resetTimer} disabled={timerState === 'ready' || isReadyEditing} shape="square"><RefreshIcon /></Button></TooltipWrapper>
        <TooltipWrapper tooltipContent={t('tournament.forceStopTournamentTooltip')}><Button onClick={tournamentActions.forceStopTournament} disabled={forceStopButtonDisabled} shape="square"><TimerOffIcon /></Button></TooltipWrapper>
      </ControlsContainer>
      <StepButtonsContainer>
        <TooltipWrapper tooltipContent={t('tournament.prevLevelTooltip')}><Button onClick={tournamentActions.prevLevel} disabled={prevLevelButtonDisabled} shape="square"><RewindIcon /></Button></TooltipWrapper>
        <StepButtonGroup>
          <TooltipWrapper tooltipContent={t('tournament.stepBackTooltip')}><Button onClick={() => tournamentActions.adjustTimer(stepSeconds)} disabled={stepBackButtonDisabled} shape="square"><StepBackIcon /></Button></TooltipWrapper>
          <Input type="number" value={stepSeconds} onChange={e => setStepSeconds(Number(e.target.value))} size="compact" overrides={StepInputOverrides} disabled={inputSecondsDisabled} /><TimeUnitLabel>{t('common.secUnit')}</TimeUnitLabel>
        </StepButtonGroup>
        <StepButtonGroup>
          <TooltipWrapper tooltipContent={t('tournament.stepForwardTooltip')}><Button onClick={() => tournamentActions.adjustTimer(-stepSeconds)} disabled={stepForwardButtonDisabled} shape="square"><StepForwardIcon /></Button></TooltipWrapper>
        </StepButtonGroup>
        <TooltipWrapper tooltipContent={t('tournament.nextLevelTooltip')}><Button onClick={() => tournamentActions.nextLevel({ isManual: true })} disabled={nextLevelButtonDisabled} shape="square"><FastForwardIcon /></Button></TooltipWrapper>
      </StepButtonsContainer>
    </SectionContainer>
  );
};

export default TimerControls;
